import Button from "@pathwright/ui/src/components/button/Button"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import HtmlText from "@pathwright/ui/src/components/text/HtmlText"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import Text from "@pathwright/ui/src/components/ui/Text"
import View from "@pathwright/ui/src/components/ui/View"
import { useTranslate } from "@pathwright/ux/src/components/lng/withTranslate"
import { Fragment, forwardRef } from "react"

const ContactLink = forwardRef(
  ({ children, href, onClick, ...triggerProps }, ref) => (
    <span ref={ref} style={{ display: "inline-block" }} {...triggerProps}>
      <Button brand styleType="text" href={href} onClick={onClick}>
        {children}
      </Button>
    </span>
  )
)
ContactLink.displayName = "ContactLink"

// Intl.ListFormat stringifies items, so index placeholders map parts back to nodes.
const joinDisjunction = (language, items) => {
  if (!items.length) return null

  const formatter = new Intl.ListFormat(language, {
    style: "long",
    type: "disjunction"
  })

  return formatter
    .formatToParts(items.map((_, index) => String(index)))
    .map((part, index) =>
      part.type === "element" ? (
        <Fragment key={index}>{items[Number(part.value)]}</Fragment>
      ) : (
        <Fragment key={index}>{part.value}</Fragment>
      )
    )
}

const SupportContactOptions = ({
  support_email,
  support_phone,
  userIsSchoolAdmin
}) => {
  const { t, tc, Trans, i18n } = useTranslate()
  const options = []
  // Ensuring first option is capitalized.
  const translateOption = (...args) => (options.length === 0 ? tc : t)(...args)

  if (window.Intercom) {
    const messageLink = (
      <ContactLink
        href="javascript:void(0)"
        onClick={userIsSchoolAdmin ? undefined : () => window.Intercom("show")}
      >
        {translateOption("support_school.send_message")}
      </ContactLink>
    )

    options.push(
      userIsSchoolAdmin ? (
        <Tooltip title={t("support_school.send_message_title")}>
          {messageLink}
        </Tooltip>
      ) : (
        messageLink
      )
    )
  }

  if (support_email) {
    options.push(
      <ContactLink href={`mailto:${support_email}`}>
        {window.Intercom
          ? translateOption("support_school.email")
          : translateOption("support_school.send_message")}
      </ContactLink>
    )
  }

  if (support_phone) {
    options.push(
      <Trans
        t={options.length === 0 ? tc : t}
        i18nKey="support_school.call_us_at"
        values={{ phone_number: support_phone }}
        components={{
          phone: <ContactLink href={`tel:${support_phone}`} />
        }}
      />
    )
  }

  if (!options.length) return null

  return (
    <span className="Support__contact-option">
      {joinDisjunction(i18n.resolvedLanguage || i18n.language || "en", options)}
    </span>
  )
}

const SupportSchool = (props) => {
  const {
    school,
    support_email,
    support_phone,
    support_link,
    support_policy,
    userIsSchoolAdmin
  } = props

  const { t } = useTranslate()

  const tFAQs = t("support_school.faqs")

  return (
    <div>
      <header>
        {school.image && <img src={school.image} />}
        <Text.H1>
          {t("support_school.title", { school_name: school.name })}
        </Text.H1>
      </header>

      <View className="Support__content" borderTop paddingTop paddingBottom>
        <ul>
          {support_link && (
            <li>
              <span>
                <Pathicon icon="check-circle" />
                <span>
                  {t("support_school.read_faqs")}&nbsp;
                  <span style={{ display: "inline-block" }}>
                    <Button
                      brand
                      styleType="text"
                      target="_blank"
                      href={support_link}
                    >
                      {tFAQs}
                    </Button>
                  </span>
                </span>
              </span>
            </li>
          )}
          {
            // dumb
            Boolean(
              support_link &&
                (support_email || window.Intercom || support_phone)
            ) && <hr />
          }
          <li>
            {Boolean(
              support_link &&
                (support_email || window.Intercom || support_phone)
            ) && (
              <span>
                <Pathicon icon="check-circle" />
                <span>{t("support_school.ask_us")}</span>
                <br />
              </span>
            )}
            {support_policy && (
              <HtmlText className="Support__policy" html={support_policy} />
            )}

            <SupportContactOptions
              support_email={support_email}
              support_phone={support_phone}
              userIsSchoolAdmin={userIsSchoolAdmin}
            />
          </li>
        </ul>
      </View>
    </div>
  )
}

export default SupportSchool
